// SelectSymptomsConstants.js
// ─── Single source of truth for the Symptom Checker Wizard ───────────────────
// Contains:
//   • COMMON_SYMPTOMS          — quick-select symptom chips
//   • LOCATION_CATEGORIES      — body-area symptom groups
//   • BODY_PARTS               — body map selector
//   • ALL_HEALTH_KEYWORDS      — voice-recognition keyword list
//   • ALL_SYMPTOM_LABELS       — flat label list for matching
//   • STANDARD_FREQUENCY_OPTIONS — PHQ-9 / GAD-7 shared options
//   • BINARY_OPTIONS           — Yes / No options
//   • SCORING_OPTIONS_3        — 0/1/2 scored options
//   • SCREENING_QUESTIONNAIRES — all 11 assessment questionnaires
//   • ASSESSMENT_TYPES         — id → questionnaireId map
//   • SYMPTOM_CLUSTERS         — symptom keyword → assessment routing
//   • ASSESSMENT_SYMPTOM_MAP   — keyword matching for routing
//   • Helper functions (exported)
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useRef, useCallback } from "react";

/* ══════════════════════════════════════════════════════════════
   COMMON OPTION SETS
══════════════════════════════════════════════════════════════ */

export const STANDARD_FREQUENCY_OPTIONS = [
  { id: "not_at_all",          label: "Not at all",              value: 0 },
  { id: "several_days",        label: "Several days",            value: 1 },
  { id: "more_than_half_days", label: "More than half the days", value: 2 },
  { id: "nearly_every_day",    label: "Nearly every day",        value: 3 },
];

// 0 / 1 / 2  — generic 3-level scored options
const opt3 = (a, b, c, v0 = 0, v1 = 1, v2 = 2) => [
  { id: "opt0", label: a, value: v0 },
  { id: "opt1", label: b, value: v1 },
  { id: "opt2", label: c, value: v2 },
];

// Shorthand aliases used across questionnaires
const NO_MILD_SIG      = opt3("No / Normal", "Mild / Occasional", "Significant / Frequent");
const RARELY_SOMETIMES = opt3("Rarely", "Sometimes", "Frequently");
const NO_MILD_FREQ     = opt3("No", "Mild", "Significant");
const NO_SOMETIMES_FREQ = opt3("No", "Sometimes", "Frequently");
const NO_OCCASIONALLY_FREQ = opt3("No", "Occasionally", "Frequently");
const LOW_MODERATE_HIGH = opt3("Low / None", "Moderate", "High / Significant");

/* ══════════════════════════════════════════════════════════════
   COMMON SYMPTOMS  (quick-select chips on StepSelectSymptoms)
══════════════════════════════════════════════════════════════ */

export const COMMON_SYMPTOMS = [
  {
    id: "fever", label: "Fever",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 1 1 4 0Z" />
        <path d="M12 13v4" /><path d="M12 9v1" />
      </svg>
    ),
  },
  {
    id: "headache", label: "Headache",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 18h12s1-6-1-10c-1.5-3-5-4-5-4s-3.5 1-5 4c-2 4-1 10-1 10z" />
        <path d="M12 2v2" /><path d="m17 5 1-1" /><path d="m7 5-1-1" />
      </svg>
    ),
  },
  {
    id: "cough", label: "Cough",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 18h4l2 3h-8l2-3z" /><path d="M12 18v-5" />
        <path d="M12 13c3 0 4-1 4-3s-1-4-4-4-4 2-4 4 1 3 4 3z" />
        <path d="M18 10h3" /><path d="M18 7l2-1" />
      </svg>
    ),
  },
  {
    id: "fatigue", label: "Fatigue",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12s4-5 10-5 10 5 10 5-4 5-10 5-10-5-10-5z" />
        <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
        <path d="M6 8l2 1" /><path d="M18 8l-2 1" />
      </svg>
    ),
  },
  {
    id: "nausea", label: "Nausea",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3c-4 0-7 2-7 6 0 3 2 4.5 2 7s-2 5-2 5h14s-2-2.5-2-5 2-4 2-7-3-6-7-6z" />
        <path d="M9 11a3 3 0 0 1 6 0" />
      </svg>
    ),
  },
  {
    id: "backpain", label: "Back Pain",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="10" y="2"  width="4" height="3" rx="1" />
        <rect x="10" y="7"  width="4" height="3" rx="1" />
        <rect x="10" y="12" width="4" height="3" rx="1" />
        <rect x="10" y="17" width="4" height="3" rx="1" />
        <path d="M8 14l-2 2" /><path d="M8 10l-2-2" />
      </svg>
    ),
  },
  {
    id: "anxiety", label: "Anxiety",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 14c1.5-1.5 3-3 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.8 0-3 1-4.5 2-1.5-1-2.7-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.5 1.5 4 3 5.5l7 7 7-7z" />
        <path d="M7 10h2l1-2 2 4 1-2h2" />
      </svg>
    ),
  },
  {
    id: "dizziness", label: "Dizziness",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a10 10 0 1 0 10 10c0-5.5-4.5-10-10-10zm0 16a6 6 0 1 1 6-6c0 3.3-2.7 6-6 6z" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    ),
  },
  {
    id: "insomnia", label: "Insomnia",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M3 12c0-5 4-8 9-8s9 3 9 8" />
        <path d="M7 16c2 1 4 1 6 0" /><path d="M8 18c1.5.5 3 .5 4.5 0" />
      </svg>
    ),
  },
  {
    id: "chestpain", label: "Chest Pain",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 4v16" />
        <path d="M18 6c-2 2-10 2-12 0" /><path d="M18 10c-2 2-10 2-12 0" />
        <path d="M18 14c-2 2-10 2-12 0" />
        <path d="M11 8l2 4-2 4" stroke="red" />
      </svg>
    ),
  },
  {
    id: "shortbreath", label: "Shortness of Breath",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 5v4" />
        <path d="M12 9c-2-2-5-2-5 3s3 6 5 6 5-1 5-6-3-5-5-3z" />
        <path d="M12 9c2-2 5-2 5 3s-3 6-5 6-5-1-5-6 3-5 5-3z" />
      </svg>
    ),
  },
  {
    id: "sorethroat", label: "Sore Throat",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 2h8c0 0 1 8-1 12s-3 8-3 8-1-4-3-8-1-12-1-12z" />
        <path d="M11 9h2" /><path d="M10 12h4" />
      </svg>
    ),
  },
];

/* ══════════════════════════════════════════════════════════════
   LOCATION CATEGORIES  (body-area symptom picker)
══════════════════════════════════════════════════════════════ */

export const LOCATION_CATEGORIES = [
  {
    id: "head", label: "Head & Neck", sub: "Headache, dizziness",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="6" /><path d="M12 14v7" /><path d="M9 17h6" />
      </svg>
    ),
    color: "#FFFFFF", iconColor: "#2563EB",
    symptoms: ["Headache", "Migraine", "Dizziness", "Sore Throat", "Neck Pain", "Ear Pain", "Eye Pain", "Neck Stiffness"],
  },
  {
    id: "chest", label: "Chest & Breathing", sub: "Chest pain, cough, wheeze",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 4v16" />
        <path d="M7 6a5 5 0 0 0 0 10c2 0 5-3 5-3s-3-7-5-7z" />
        <path d="M17 6a5 5 0 0 1 0 10c-2 0-5-3-5-3s3-7 5-7z" />
      </svg>
    ),
    color: "#FFFFFF", iconColor: "#0284C7",
    symptoms: ["Chest Pain", "Shortness of Breath", "Cough", "Wheezing", "Palpitations", "Tightness"],
  },
  {
    id: "abdomen", label: "Abdomen & Digestion", sub: "Nausea, cramps, bloating",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 10c0-2 2-3 4-3s4 1 4 3-2 3-4 3-4 1-4 3 2 3 4 3 4-1 4-3" />
        <rect x="6" y="8" width="12" height="10" rx="2" opacity="0.3" />
      </svg>
    ),
    color: "#FFFFFF", iconColor: "#EA580C",
    symptoms: ["Stomach Pain", "Bloating", "Nausea", "Vomiting", "Constipation", "Diarrhea", "Indigestion"],
  },
  {
    id: "pelvic", label: "Pelvic & Reproductive", sub: "Period changes, pelvic pain",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 6c2-1 4 0 4 3 0 4 4 7 4 7s4-3 4-7c0-3 2-4 4-3 0 5-4 10-8 10S4 11 4 6z" />
      </svg>
    ),
    color: "#FFFFFF", iconColor: "#A21CAF",
    symptoms: ["Pelvic Pain", "Irregular Periods", "Heavy Bleeding", "Spotting", "Cramping", "Discharge"],
  },
  {
    id: "back", label: "Back & Musculoskeletal", sub: "Back pain, muscle aches",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v14" />
        <path d="M10 20c0-2 4-2 4 0s-4 2-4 0z" />
        <path d="M8 5h8M8 9h8M8 13h8" />
      </svg>
    ),
    color: "#FFFFFF", iconColor: "#15803D",
    symptoms: ["Lower Back Pain", "Upper Back Pain", "Joint Pain", "Muscle Ache", "Stiffness", "Swelling"],
  },
  {
    id: "skin", label: "Skin & Allergy", sub: "Rash, itching, swelling",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 8h16" /><path d="M4 12h16" /><path d="M4 16h16" />
        <circle cx="12" cy="6" r="1" /><circle cx="7" cy="5" r="1" /><circle cx="17" cy="5" r="1" />
      </svg>
    ),
    color: "#FFFFFF", iconColor: "#BE123C",
    symptoms: ["Rash", "Itching", "Hives", "Redness", "Swelling", "Dryness", "Bruising"],
  },
  {
    id: "fevercat", label: "Fever & Infection", sub: "Fever, chills, body ache",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v4l2 2" />
        <circle cx="8" cy="9" r="1" fill="currentColor" />
        <circle cx="15" cy="12" r="1" fill="currentColor" />
      </svg>
    ),
    color: "#FFFFFF", iconColor: "#B45309",
    symptoms: ["Fever", "Chills", "Body Ache", "Night Sweats", "Fatigue", "Weakness"],
  },
  {
    id: "mental", label: "Mental & Emotional", sub: "Stress, anxiety, low mood",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 21c-4.5 0-8-3.5-8-8s3.5-8 8-8 8 3.5 8 8-3.5 8-8 8z" />
        <path d="M12 5v16" />
        <path d="M12 13c-2 0-4-1-4-3s2-3 4-3" />
        <path d="M12 13c2 0 4-1 4-3s-2-3-4-3" />
      </svg>
    ),
    color: "#FFFFFF", iconColor: "#0D9488",
    symptoms: ["Anxiety", "Depression", "Stress", "Mood Swings", "Irritability", "Panic Attacks"],
  },
];

/* ══════════════════════════════════════════════════════════════
   BODY PARTS  (body-map selector)
══════════════════════════════════════════════════════════════ */

export const BODY_PARTS = [
  { id: "head",    label: "Head",    icon: "👤" },
  { id: "chest",   label: "Chest",   icon: "🫁" },
  { id: "abdomen", label: "Abdomen", icon: "🫃" },
  { id: "pelvic",  label: "Pelvic",  icon: "🦴" },
  { id: "back",    label: "Back",    icon: "🔙" },
  { id: "legs",    label: "Legs",    icon: "🦵" },
  { id: "skin",    label: "Skin",    icon: "🩹" },
];

/* ══════════════════════════════════════════════════════════════
   KEYWORD LISTS  (voice recognition + text matching)
══════════════════════════════════════════════════════════════ */

export const ALL_SYMPTOM_LABELS = [
  ...new Set([
    ...COMMON_SYMPTOMS.map((x) => x.label),
    ...LOCATION_CATEGORIES.flatMap((c) => c.symptoms),
  ]),
];

export const ALL_HEALTH_KEYWORDS = [
  ...new Set([
    ...COMMON_SYMPTOMS.map((s) => s.label.toLowerCase()),
    ...LOCATION_CATEGORIES.flatMap((c) => c.symptoms.map((s) => s.toLowerCase())),
    "pain", "ache", "burning", "tingling", "numbness", "cramps", "spasm",
    "bleeding", "discharge", "swelling", "bruising", "rash", "itching",
    "fever", "chills", "fatigue", "nausea", "vomiting", "diarrhea",
    "cough", "wheezing", "palpitation", "migraine", "insomnia",
    "anxiety", "depression", "stress", "dizziness", "vertigo",
    "thyroid", "pcos", "menopause", "pregnancy", "hormonal",
    "diabetes", "blood sugar", "blood pressure", "weight gain",
    "hair loss", "skin changes", "mood swings", "hot flashes",
  ]),
];

/* ══════════════════════════════════════════════════════════════
   SYMPTOM → ASSESSMENT ROUTING MAP
══════════════════════════════════════════════════════════════ */

const ASSESSMENT_SYMPTOM_MAP = {
  depression: [
    "depression", "depressed", "low mood", "hopeless", "sadness", "sad",
    "fatigue", "insomnia", "sleep", "stress", "burnout", "loss of interest",
    "mood swings", "worthlessness", "concentration", "psychomotor",
  ],
  anxiety: [
    "anxiety", "anxious", "panic", "panic attacks", "stress", "fear", "worry",
    "restless", "restlessness", "palpitations", "insomnia", "irritability",
    "dizziness", "nervousness", "on edge",
  ],
  fertility: [
    "irregular periods", "pcos", "infertility", "difficulty conceiving",
    "miscarriage", "fertility", "ovulation", "hormonal imbalance",
    "menstrual irregularity", "hair thinning", "acne", "excess hair",
  ],
  thyroid: [
    "thyroid", "weight gain", "weight loss", "fatigue", "cold intolerance",
    "heat intolerance", "hair loss", "dry skin", "palpitations", "tremors",
    "brain fog", "irregular periods", "slow heart rate", "sweating",
  ],
  lifestyle: [
    "sedentary", "no exercise", "junk food", "smoking", "alcohol",
    "overweight", "obesity", "poor diet", "dehydration",
    "preventive care", "no checkup",
  ],
  hormonal: [
    "hormonal imbalance", "irregular periods", "heavy bleeding", "pms",
    "mood swings", "weight gain", "acne", "hair loss", "fatigue",
    "hot flashes", "night sweats", "difficulty conceiving",
  ],
  diabetes: [
    "diabetes", "blood sugar", "sugar craving", "frequent urination",
    "excessive thirst", "gestational diabetes", "pcos", "overweight",
    "family history diabetes", "hypertension",
  ],
  bmi: [
    "overweight", "obesity", "bmi", "weight gain", "abdominal fat",
    "waist circumference", "metabolic", "blood pressure", "prediabetes",
  ],
  sleep: [
    "insomnia", "poor sleep", "sleep issues", "night awakenings", "fatigue",
    "daytime sleepiness", "not refreshed", "sleep disturbance",
    "hot flashes", "night sweats",
  ],
  stress: [
    "stress", "overwhelmed", "burnout", "emotional exhaustion", "irritability",
     "sleep problems", "headache", "muscle tension",
    "work pressure", "caregiving", "pms", "menopause",
  ], // "anxiety"
  social: [
    "housing", "financial stress", "food insecurity", "transportation",
    "social support", "domestic violence", "health literacy", "caregiving burden",
    "social isolation",
  ],
  general: [
    "general health", "checkup", "screening", "preventive", "fatigue",
    "weight", "diet", "sleep", "stress", "hormonal", "chronic condition",
    "smoking", "alcohol",
  ],
};

/* ══════════════════════════════════════════════════════════════
   SCREENING QUESTIONNAIRES  — all 11 assessments
══════════════════════════════════════════════════════════════ */

export const SCREENING_QUESTIONNAIRES = {

  /* ──────────────────────────────────────────────────────────
     1. PHQ-9  —  Depression
  ────────────────────────────────────────────────────────── */
  depression: {
    id: "phq9",
    type: "mental_health",
    title: "Depression Screening",
    shortTitle: "PHQ-9",
    icon: "🧠",
    description: "Clinically validated PHQ-9 screening tool for depression severity.",
    instructions: "Over the last 2 weeks, how often have you been bothered by the following problems?",
    category: ["Depression", "Mood", "Mental Wellness", "Women's Health"],
    womensHealthContext: ["Pregnancy", "Postpartum", "Menopause", "Hormonal Disorders"],
    scoring: {
      min: 0,
      max: 27,
      interpretation: {
        clinicalThreshold: 10,
        urgentRule: "Any score > 0 on Question 9 requires immediate clinical attention.",
      },
      severity: [
        { id: "minimal",           label: "Minimal",           min: 0,  max: 4,  color: "#16A34A", recommendation: "Monitor symptoms and maintain healthy lifestyle habits." },
        { id: "mild",              label: "Mild",              min: 5,  max: 9,  color: "#65A30D", recommendation: "Lifestyle support and emotional wellness strategies recommended." },
        { id: "moderate",          label: "Moderate",          min: 10, max: 14, color: "#D97706", recommendation: "Professional clinical evaluation is recommended." },
        { id: "moderately_severe", label: "Moderately Severe", min: 15, max: 19, color: "#EA580C", recommendation: "Mental health consultation strongly recommended." },
        { id: "severe",            label: "Severe",            min: 20, max: 27, color: "#DC2626", recommendation: "Immediate psychiatric or psychological care recommended." },
      ],
    },
    questions: [
      { id: "q1", order: 1, text: "Little interest or pleasure in doing things",               clinicalTag: "anhedonia",            options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q2", order: 2, text: "Feeling down, depressed, or hopeless",                      clinicalTag: "depressed_mood",        options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q3", order: 3, text: "Trouble falling or staying asleep, or sleeping too much",   clinicalTag: "sleep_disturbance",     options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q4", order: 4, text: "Feeling tired or having little energy",                     clinicalTag: "fatigue",               options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q5", order: 5, text: "Poor appetite or overeating",                               clinicalTag: "appetite_changes",      options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q6", order: 6, text: "Feeling bad about yourself — or that you are a failure or have let yourself or your family down", clinicalTag: "worthlessness", options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q7", order: 7, text: "Trouble concentrating on things, such as reading or watching television", clinicalTag: "concentration_issues", options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q8", order: 8, text: "Moving or speaking so slowly that other people could have noticed, or being so fidgety or restless that you have been moving around more than usual", clinicalTag: "psychomotor_changes", options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q9", order: 9, text: "Thoughts that you would be better off dead or of hurting yourself in some way", clinicalTag: "suicidal_thoughts", critical: true, escalationRequired: true, options: STANDARD_FREQUENCY_OPTIONS },
    ],
  },

  /* ──────────────────────────────────────────────────────────
     2. GAD-7  —  Anxiety
  ────────────────────────────────────────────────────────── */
  anxiety: {
    id: "gad7",
    type: "mental_health",
    title: "Anxiety Screening",
    shortTitle: "GAD-7",
    icon: "💙",
    description: "Clinically validated GAD-7 screening tool for generalized anxiety severity.",
    instructions: "Over the last 2 weeks, how often have you been bothered by the following problems?",
    category: ["Anxiety", "Stress", "Mental Wellness", "Women's Health"],
    womensHealthContext: ["Pregnancy", "Postpartum", "Menopause", "Hormonal Disorders"],
    scoring: {
      min: 0,
      max: 21,
      interpretation: {
        clinicalThreshold: 10,
        urgentRule: "Higher scores indicate clinically significant anxiety requiring evaluation.",
      },
      severity: [
        { id: "minimal",  label: "Minimal",  min: 0,  max: 4,  color: "#16A34A", recommendation: "Continue healthy lifestyle and stress management." },
        { id: "mild",     label: "Mild",     min: 5,  max: 9,  color: "#65A30D", recommendation: "Monitor symptoms and practice stress reduction techniques." },
        { id: "moderate", label: "Moderate", min: 10, max: 14, color: "#D97706", recommendation: "Clinical evaluation may be beneficial." },
        { id: "severe",   label: "Severe",   min: 15, max: 21, color: "#DC2626", recommendation: "Professional mental health support strongly recommended." },
      ],
    },
    questions: [
      { id: "q1", order: 1, text: "Feeling nervous, anxious, or on edge",                                clinicalTag: "nervousness",          options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q2", order: 2, text: "Not being able to stop or control worrying",                           clinicalTag: "uncontrolled_worry",   options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q3", order: 3, text: "Worrying too much about different things",                             clinicalTag: "excessive_worry",      options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q4", order: 4, text: "Trouble relaxing",                                                     clinicalTag: "difficulty_relaxing",  options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q5", order: 5, text: "Being so restless that it is hard to sit still",                       clinicalTag: "restlessness",         options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q6", order: 6, text: "Becoming easily annoyed or irritable",                                 clinicalTag: "irritability",         options: STANDARD_FREQUENCY_OPTIONS },
      { id: "q7", order: 7, text: "Feeling afraid as if something awful might happen",                    clinicalTag: "fear_of_disaster",     options: STANDARD_FREQUENCY_OPTIONS },
    ],
  },

  /* ──────────────────────────────────────────────────────────
     3. Fertility Risk Screening
  ────────────────────────────────────────────────────────── */
  fertility: {
    id: "fertility_screen",
    type: "reproductive_health",
    title: "Fertility Risk Screening",
    shortTitle: "Fertility",
    icon: "🌱",
    description: "Screens for common factors affecting female fertility.",
    instructions: "Please answer based on your current health status or the last 6–12 months.",
    category: ["Fertility", "Reproductive Health", "Hormonal Health"],
    womensHealthContext: ["PCOS", "Menstrual Disorders", "Hormonal Imbalance"],
    scoring: {
      min: 0,
      max: 12,
      interpretation: { clinicalThreshold: 4 },
      severity: [
        { id: "low",      label: "Low Risk",      min: 0, max: 3,  color: "#16A34A", recommendation: "Maintain healthy lifestyle. Consider preconception counselling." },
        { id: "moderate", label: "Moderate Risk",  min: 4, max: 7,  color: "#D97706", recommendation: "Consult a gynaecologist or fertility specialist." },
        { id: "high",     label: "High Risk",      min: 8, max: 12, color: "#DC2626", recommendation: "Comprehensive fertility evaluation strongly recommended." },
      ],
    },
    questions: [
      {
        id: "q1", order: 1, text: "Are your menstrual cycles regular (typically every 21–35 days)?",
        clinicalTag: "menstrual_regularity",
        options: [
          { id: "regular",      label: "Regular (every 21–35 days)",  value: 0 },
          { id: "slightly",     label: "Slightly irregular",           value: 1 },
          { id: "very_irreg",   label: "Very irregular or absent",     value: 2 },
        ],
      },
      {
        id: "q2", order: 2, text: "Do you have significant weight gain/loss, obesity, or difficulty maintaining a healthy weight?",
        clinicalTag: "weight_metabolic",
        options: [
          { id: "no",  label: "No",          value: 0 },
          { id: "mil", label: "Mild",        value: 1 },
          { id: "sig", label: "Significant", value: 2 },
        ],
      },
      {
        id: "q3", order: 3, text: "Do you experience acne, excess facial/body hair, hair thinning, or have diagnosed PCOS?",
        clinicalTag: "pcos_hormonal",
        options: [
          { id: "no",  label: "No",                         value: 0 },
          { id: "mil", label: "Mild symptoms",              value: 1 },
          { id: "sig", label: "Significant / Diagnosed PCOS", value: 2 },
        ],
      },
      {
        id: "q4", order: 4, text: "Do stress, anxiety, or emotional wellbeing affect your health or fertility planning?",
        clinicalTag: "stress_emotional",
        options: RARELY_SOMETIMES,
      },
      {
        id: "q5", order: 5, text: "Do you smoke, consume alcohol frequently, or have poor sleep or a sedentary lifestyle?",
        clinicalTag: "lifestyle_factors",
        options: [
          { id: "none", label: "None",             value: 0 },
          { id: "mild", label: "Mild / moderate",  value: 1 },
          { id: "sig",  label: "Significant",      value: 2 },
        ],
      },
      {
        id: "q6", order: 6, text: "Have you experienced difficulty conceiving (>12 months trying), miscarriage, or fertility treatment?",
        clinicalTag: "reproductive_history",
        options: [
          { id: "no",       label: "No",                          value: 0 },
          { id: "one",      label: "One issue",                   value: 1 },
          { id: "multiple", label: "Multiple / significant issues", value: 2 },
        ],
      },
    ],
  },

  /* ──────────────────────────────────────────────────────────
     4. Thyroid Screening
  ────────────────────────────────────────────────────────── */
  thyroid: {
    id: "thyroid_screen",
    type: "hormonal_metabolic",
    title: "Thyroid Screening",
    shortTitle: "Thyroid",
    icon: "🦋",
    description: "Screens for symptoms suggestive of hypothyroidism, hyperthyroidism, or thyroid imbalance.",
    instructions: "Over the last 2–3 months, how often have you experienced the following?",
    category: ["Thyroid", "Hormonal Health", "Metabolism"],
    womensHealthContext: ["Menstrual Health", "Fertility", "Pregnancy", "Weight Management"],
    scoring: {
      min: 0,
      max: 14,
      interpretation: { clinicalThreshold: 5 },
      severity: [
        { id: "low",      label: "Low Risk",      min: 0, max: 4,  color: "#16A34A", recommendation: "No immediate concern. Monitor symptoms." },
        { id: "moderate", label: "Moderate Risk",  min: 5, max: 8,  color: "#D97706", recommendation: "Thyroid function tests (TSH, T3, T4) recommended." },
        { id: "high",     label: "High Risk",      min: 9, max: 14, color: "#DC2626", recommendation: "Urgent thyroid evaluation by endocrinologist recommended." },
      ],
    },
    questions: [
      { id: "q1", order: 1, text: "Unexplained weight gain or difficulty losing weight, or sudden unexplained weight loss", clinicalTag: "weight_metabolism",     options: NO_MILD_FREQ },
      { id: "q2", order: 2, text: "Constant tiredness or low energy despite adequate rest",                                  clinicalTag: "fatigue_energy",        options: RARELY_SOMETIMES },
      { id: "q3", order: 3, text: "Feeling unusually cold when others are warm, or unusually hot",                          clinicalTag: "temperature_sensitivity", options: NO_MILD_FREQ },
      { id: "q4", order: 4, text: "Irregular periods, heavy or light bleeding, or fertility concerns",                       clinicalTag: "menstrual_hormonal",    options: NO_MILD_FREQ },
      { id: "q5", order: 5, text: "Hair thinning, hair fall, dry skin, or brittle nails",                                   clinicalTag: "skin_hair",             options: NO_MILD_FREQ },
      { id: "q6", order: 6, text: "Brain fog, anxiety, depression/low mood, or irritability",                               clinicalTag: "mood_mental",           options: RARELY_SOMETIMES },
      { id: "q7", order: 7, text: "Palpitations, slow heart rate, tremors, or excessive sweating",                          clinicalTag: "heart_physical",        options: NO_MILD_FREQ },
    ],
  },

  /* ──────────────────────────────────────────────────────────
     5. Lifestyle Risk Assessment
  ────────────────────────────────────────────────────────── */
  lifestyle: {
    id: "lifestyle_screen",
    type: "lifestyle_preventive",
    title: "Lifestyle Risk Assessment",
    shortTitle: "Lifestyle",
    icon: "🏃",
    description: "Screens lifestyle factors aligned with WHO/IPAQ guidelines.",
    instructions: "Please select the option that best describes your current habits.",
    category: ["Lifestyle", "Preventive Health", "General Wellness"],
    womensHealthContext: ["All Women", "Chronic Disease Prevention"],
    scoring: {
      min: 0,
      max: 20,
      interpretation: { clinicalThreshold: 9 },
      severity: [
        { id: "low",      label: "Low Risk",      min: 0,  max: 8,  color: "#16A34A", recommendation: "Maintain healthy habits. Annual health check-up advised." },
        { id: "moderate", label: "Moderate Risk",  min: 9,  max: 18, color: "#D97706", recommendation: "Lifestyle modification program recommended." },
        { id: "high",     label: "High Risk",      min: 19, max: 20, color: "#DC2626", recommendation: "Urgent lifestyle intervention and medical review recommended." },
      ],
    },
    questions: [
      {
        id: "q1", order: 1, text: "Total physical activity per week (aligned with IPAQ guidelines)",
        clinicalTag: "activity_minutes",
        options: [
          { id: "high",     label: "≥150 minutes/week",   value: 0 },
          { id: "moderate", label: "60–149 minutes/week", value: 1 },
          { id: "low",      label: "<60 minutes/week",    value: 2 },
        ],
      },
      {
        id: "q2", order: 2, text: "Daily sitting/sedentary time",
        clinicalTag: "sitting_time",
        options: [
          { id: "low",      label: "<6 hours/day",  value: 0 },
          { id: "moderate", label: "6–8 hours/day", value: 1 },
          { id: "high",     label: ">8 hours/day",  value: 2 },
        ],
      },
      {
        id: "q3", order: 3, text: "Daily fruits and vegetables intake",
        clinicalTag: "fruits_veg",
        options: [
          { id: "daily",     label: "Daily (≥5 servings)", value: 0 },
          { id: "sometimes", label: "Sometimes",           value: 1 },
          { id: "rarely",    label: "Rarely",              value: 2 },
        ],
      },
      {
        id: "q4", order: 4, text: "Junk or processed food consumption",
        clinicalTag: "junk_food",
        options: [
          { id: "rare",     label: "Rare",     value: 0 },
          { id: "weekly",   label: "Weekly",   value: 1 },
          { id: "frequent", label: "Frequent", value: 2 },
        ],
      },
      {
        id: "q5", order: 5, text: "Daily water intake",
        clinicalTag: "water_intake",
        options: [
          { id: "adequate",  label: "≥2 litres/day", value: 0 },
          { id: "moderate",  label: "1–2 litres",    value: 1 },
          { id: "low",       label: "<1 litre",      value: 2 },
        ],
      },
      {
        id: "q6", order: 6, text: "Smoking",
        clinicalTag: "smoking",
        options: [
          { id: "no",         label: "No",         value: 0 },
          { id: "occasional", label: "Occasional", value: 1 },
          { id: "regular",    label: "Regular",    value: 2 },
        ],
      },
      {
        id: "q7", order: 7, text: "Alcohol consumption",
        clinicalTag: "alcohol",
        options: [
          { id: "no",         label: "No",         value: 0 },
          { id: "occasional", label: "Occasional", value: 1 },
          { id: "frequent",   label: "Frequent",   value: 2 },
        ],
      },
      {
        id: "q8", order: 8, text: "Existing chronic conditions (e.g. thyroid disorder, hypertension)",
        clinicalTag: "chronic_conditions",
        options: [
          { id: "none",     label: "None",                       value: 0 },
          { id: "one",      label: "One condition",              value: 1 },
          { id: "multiple", label: "Multiple conditions",        value: 2 },
        ],
      },
      {
        id: "q9", order: 9, text: "Frequency of health check-ups",
        clinicalTag: "health_checkups",
        options: [
          { id: "regular",    label: "Regular",    value: 0 },
          { id: "occasional", label: "Occasional", value: 1 },
          { id: "none",       label: "None",       value: 2 },
        ],
      },
      {
        id: "q10", order: 10, text: "Preventive screenings (Pap smear, breast exam, blood tests)",
        clinicalTag: "preventive_screenings",
        options: [
          { id: "up_to_date", label: "Up to date", value: 0 },
          { id: "delayed",    label: "Delayed",    value: 1 },
          { id: "never",      label: "Never",      value: 2 },
        ],
      },
    ],
  },

  /* ──────────────────────────────────────────────────────────
     6. Hormonal Imbalance Screening
  ────────────────────────────────────────────────────────── */
  hormonal: {
    id: "hormonal_screen",
    type: "hormonal_reproductive",
    title: "Hormonal Imbalance Screening",
    shortTitle: "Hormonal",
    icon: "⚗️",
    description: "Screens for signs of hormonal imbalance across key women's health domains.",
    instructions: "Over the last 2–3 months, choose what applies. Score: 0 = No/Normal, 1 = Mild/Occasional, 2 = Frequent/Severe.",
    category: ["Hormonal Health", "Reproductive Health", "Women's Wellness"],
    womensHealthContext: ["PCOS", "Menopause", "Thyroid", "Adrenal"],
    scoring: {
      min: 0,
      max: 12,
      interpretation: { clinicalThreshold: 4 },
      severity: [
        { id: "low",      label: "Low Risk",      min: 0, max: 3,  color: "#16A34A", recommendation: "No immediate concern. Maintain healthy lifestyle." },
        { id: "moderate", label: "Moderate Risk",  min: 4, max: 7,  color: "#D97706", recommendation: "Hormonal blood panel recommended." },
        { id: "high",     label: "High Risk",      min: 8, max: 12, color: "#DC2626", recommendation: "Endocrinologist or gynaecologist consultation recommended." },
      ],
    },
    questions: [
      { id: "q1", order: 1, text: "Irregular or missed periods, very heavy/light bleeding, severe menstrual pain, PMS, or difficulty conceiving (>1 year trying)", clinicalTag: "menstrual_irregularity", options: NO_MILD_SIG },
      { id: "q2", order: 2, text: "Unexplained weight gain/loss, difficulty losing weight, increased abdominal fat, or sugar cravings", clinicalTag: "weight_metabolic", options: NO_MILD_SIG },
      { id: "q3", order: 3, text: "Persistent acne, excess facial or body hair, or hair thinning/hair loss", clinicalTag: "skin_hair_androgenic", options: NO_MILD_SIG },
      { id: "q4", order: 4, text: "Constant tiredness, low energy despite rest, or sluggishness", clinicalTag: "energy_fatigue", options: NO_MILD_SIG },
      { id: "q5", order: 5, text: "Mood swings, anxiety, irritability, low mood, or lack of interest in things you normally enjoy", clinicalTag: "mood_emotional", options: NO_MILD_SIG },
      { id: "q6", order: 6, text: "Heat/cold intolerance, hot flashes, or night sweats", clinicalTag: "temperature_hormonal", options: NO_MILD_SIG },
    ],
  },

  /* ──────────────────────────────────────────────────────────
     7. Diabetes Risk Screening
  ────────────────────────────────────────────────────────── */
  diabetes: {
    id: "diabetes_screen",
    type: "metabolic_endocrine",
    title: "Diabetes Risk Screening",
    shortTitle: "Diabetes",
    icon: "💉",
    description: "Screens for diabetes and prediabetes risk factors in women.",
    instructions: "Answer based on your current health status. Score: 0 = Low/No risk, 1 = Moderate, 2 = High risk.",
    category: ["Diabetes", "Metabolic Health", "Preventive Care"],
    womensHealthContext: ["PCOS", "Gestational Diabetes", "Menopause"],
    scoring: {
      min: 0,
      max: 14,
      interpretation: { clinicalThreshold: 5 },
      severity: [
        { id: "low",      label: "Low Risk",      min: 0, max: 4,  color: "#16A34A", recommendation: "Maintain healthy diet and exercise routine." },
        { id: "moderate", label: "Moderate Risk",  min: 5, max: 8,  color: "#D97706", recommendation: "Blood sugar testing (HbA1c, fasting glucose) recommended." },
        { id: "high",     label: "High Risk",      min: 9, max: 14, color: "#DC2626", recommendation: "Urgent diabetes screening by physician recommended." },
      ],
    },
    questions: [
      {
        id: "q1", order: 1, text: "Body Mass Index (BMI)",
        clinicalTag: "bmi",
        options: [
          { id: "normal",     label: "BMI < 25",      value: 0 },
          { id: "overweight", label: "BMI 25–29.9",   value: 1 },
          { id: "obese",      label: "BMI ≥ 30",      value: 2 },
        ],
      },
      {
        id: "q2", order: 2, text: "Physical activity level per week",
        clinicalTag: "physical_activity",
        options: [
          { id: "active",    label: "≥150 min/week",          value: 0 },
          { id: "some",      label: "Some activity",           value: 1 },
          { id: "sedentary", label: "Sedentary (<60 min/week)", value: 2 },
        ],
      },
      {
        id: "q3", order: 3, text: "Diet quality",
        clinicalTag: "diet_quality",
        options: [
          { id: "healthy",  label: "Healthy (fruits/veg daily, low sugar)", value: 0 },
          { id: "moderate", label: "Moderate",                              value: 1 },
          { id: "poor",     label: "High sugar or junk food intake",        value: 2 },
        ],
      },
      {
        id: "q4", order: 4, text: "Family history of diabetes",
        clinicalTag: "family_history",
        options: [
          { id: "no",       label: "No",                          value: 0 },
          { id: "one",      label: "One parent or sibling",       value: 1 },
          { id: "both",     label: "Both parents",                value: 2 },
        ],
      },
      {
        id: "q5", order: 5, text: "Women-specific risk factors",
        clinicalTag: "women_specific",
        options: [
          { id: "none",       label: "None",                                          value: 0 },
          { id: "pcos",       label: "PCOS or irregular periods",                     value: 1 },
          { id: "gestational",label: "History of gestational diabetes or large baby (>4 kg)", value: 2 },
        ],
      },
      {
        id: "q6", order: 6, text: "Blood pressure / metabolic health",
        clinicalTag: "blood_pressure",
        options: [
          { id: "normal",     label: "Normal blood pressure",        value: 0 },
          { id: "borderline", label: "Borderline",                   value: 1 },
          { id: "hypertension",label: "Hypertension / on medication", value: 2 },
        ],
      },
      {
        id: "q7", order: 7, text: "Age factor",
        clinicalTag: "age_risk",
        options: [
          { id: "young",  label: "Under 35 years", value: 0 },
          { id: "middle", label: "35–49 years",    value: 1 },
          { id: "older",  label: "50 years or over",value: 2 },
        ],
      },
    ],
  },

  /* ──────────────────────────────────────────────────────────
     8. BMI & Metabolic Risk
  ────────────────────────────────────────────────────────── */
  bmi: {
    id: "bmi_screen",
    type: "metabolic",
    title: "BMI & Metabolic Risk Assessment",
    shortTitle: "BMI/Metabolic",
    icon: "⚖️",
    description: "Assesses body weight, central obesity, and metabolic risk factors.",
    instructions: "Answer based on your current status or the last 3 months. Score: 0 = Low risk, 1 = Moderate, 2 = High risk.",
    category: ["BMI", "Metabolic Health", "Cardiovascular Risk"],
    womensHealthContext: ["PCOS", "Menopause", "Gestational Diabetes"],
    scoring: {
      min: 0,
      max: 14,
      interpretation: { clinicalThreshold: 5 },
      severity: [
        { id: "low",      label: "Low Risk",      min: 0, max: 4,  color: "#16A34A", recommendation: "Maintain healthy weight and lifestyle." },
        { id: "moderate", label: "Moderate Risk",  min: 5, max: 8,  color: "#D97706", recommendation: "Dietitian consultation and metabolic panel recommended." },
        { id: "high",     label: "High Risk",      min: 9, max: 14, color: "#DC2626", recommendation: "Urgent metabolic and cardiovascular risk review recommended." },
      ],
    },
    questions: [
      {
        id: "q1", order: 1, text: "Body Mass Index (BMI)",
        clinicalTag: "bmi",
        options: [
          { id: "normal",     label: "BMI < 25",     value: 0 },
          { id: "overweight", label: "BMI 25–29.9",  value: 1 },
          { id: "obese",      label: "BMI ≥ 30",     value: 2 },
        ],
      },
      {
        id: "q2", order: 2, text: "Waist circumference (central obesity — especially important for women)",
        clinicalTag: "waist_circumference",
        options: [
          { id: "low",       label: "Under 80 cm",    value: 0 },
          { id: "moderate",  label: "80–88 cm",       value: 1 },
          { id: "high",      label: "Over 88 cm",     value: 2 },
        ],
      },
      {
        id: "q3", order: 3, text: "Physical activity (WHO recommendation: ≥150 min/week)",
        clinicalTag: "physical_activity",
        options: [
          { id: "adequate",  label: "≥150 min/week",  value: 0 },
          { id: "some",      label: "60–149 min/week", value: 1 },
          { id: "low",       label: "Under 60 min/week", value: 2 },
        ],
      },
      {
        id: "q4", order: 4, text: "Diet quality",
        clinicalTag: "diet_quality",
        options: [
          { id: "healthy",  label: "Daily fruits & vegetables, low sugar", value: 0 },
          { id: "moderate", label: "Moderate diet",                        value: 1 },
          { id: "poor",     label: "High sugar or processed foods",        value: 2 },
        ],
      },
      {
        id: "q5", order: 5, text: "Blood pressure",
        clinicalTag: "blood_pressure",
        options: [
          { id: "normal",     label: "Normal",                      value: 0 },
          { id: "borderline", label: "Borderline",                  value: 1 },
          { id: "high",       label: "High or on medication",       value: 2 },
        ],
      },
      {
        id: "q6", order: 6, text: "Blood sugar / diabetes status",
        clinicalTag: "blood_sugar",
        options: [
          { id: "normal",      label: "Normal",       value: 0 },
          { id: "prediabetes", label: "Prediabetes",  value: 1 },
          { id: "diabetes",    label: "Diabetes",     value: 2 },
        ],
      },
      {
        id: "q7", order: 7, text: "Women-specific metabolic risk",
        clinicalTag: "women_metabolic",
        options: [
          { id: "none",       label: "None",                                  value: 0 },
          { id: "pcos",       label: "PCOS or irregular cycles",              value: 1 },
          { id: "gestational",label: "History of gestational diabetes",       value: 2 },
        ],
      },
    ],
  },

  /* ──────────────────────────────────────────────────────────
     9. Sleep Quality Assessment
  ────────────────────────────────────────────────────────── */
  sleep: {
    id: "sleep_screen",
    type: "sleep_wellness",
    title: "Sleep Quality Assessment",
    shortTitle: "Sleep",
    icon: "🌙",
    description: "Assesses sleep quality, duration, and hormonal influences on sleep.",
    instructions: "Over the last 2–4 weeks, select the most appropriate option. Score: 0 = Good, 1 = Mild issue, 2 = Poor.",
    category: ["Sleep", "Fatigue", "General Wellness"],
    womensHealthContext: ["Menopause", "PCOS", "Postpartum", "PMS"],
    scoring: {
      min: 0,
      max: 12,
      interpretation: { clinicalThreshold: 4 },
      severity: [
        { id: "good",     label: "Good Sleep Quality",     min: 0, max: 3,  color: "#16A34A", recommendation: "Maintain current sleep habits." },
        { id: "moderate", label: "Moderate Sleep Quality", min: 4, max: 7,  color: "#D97706", recommendation: "Sleep hygiene programme recommended." },
        { id: "poor",     label: "Poor Sleep Quality",     min: 8, max: 12, color: "#DC2626", recommendation: "Specialist sleep or hormonal evaluation recommended." },
      ],
    },
    questions: [
      {
        id: "q1", order: 1, text: "Average sleep duration per night",
        clinicalTag: "sleep_duration",
        options: [
          { id: "good", label: "7–8 hours",   value: 0 },
          { id: "mild", label: "5–6 hours",   value: 1 },
          { id: "poor", label: "Under 5 hours", value: 2 },
        ],
      },
      {
        id: "q2", order: 2, text: "Time to fall asleep (sleep onset)",
        clinicalTag: "sleep_onset",
        options: [
          { id: "fast",     label: "Under 20 minutes", value: 0 },
          { id: "moderate", label: "20–40 minutes",    value: 1 },
          { id: "slow",     label: "Over 40 minutes",  value: 2 },
        ],
      },
      {
        id: "q3", order: 3, text: "Night awakenings",
        clinicalTag: "night_awakenings",
        options: [
          { id: "rare",      label: "Rare or none",        value: 0 },
          { id: "occasional",label: "1–2 times per night", value: 1 },
          { id: "frequent",  label: "Frequent awakenings", value: 2 },
        ],
      },
      {
        id: "q4", order: 4, text: "How do you feel upon waking?",
        clinicalTag: "sleep_perception",
        options: [
          { id: "restful",    label: "Restful and refreshed",  value: 0 },
          { id: "sometimes",  label: "Sometimes tired",        value: 1 },
          { id: "unrefreshed",label: "Not refreshed at all",   value: 2 },
        ],
      },
      {
        id: "q5", order: 5, text: "Daytime fatigue or sleepiness",
        clinicalTag: "daytime_fatigue",
        options: [
          { id: "none",      label: "No daytime fatigue",       value: 0 },
          { id: "occasional",label: "Occasional tiredness",     value: 1 },
          { id: "daily",     label: "Daily fatigue or sleepiness", value: 2 },
        ],
      },
      {
        id: "q6", order: 6, text: "Do hormonal changes affect your sleep? (e.g. PMS, pregnancy, menopause hot flashes/night sweats)",
        clinicalTag: "hormonal_sleep",
        options: [
          { id: "no",        label: "No issues",                               value: 0 },
          { id: "pms",       label: "PMS or pregnancy-related disturbance",    value: 1 },
          { id: "menopause", label: "Menopause symptoms (hot flashes/night sweats)", value: 2 },
        ],
      },
    ],
  },

  /* ──────────────────────────────────────────────────────────
     10. Stress Assessment
  ────────────────────────────────────────────────────────── */
  stress: {
    id: "stress_screen",
    type: "mental_health_lifestyle",
    title: "Stress Assessment",
    shortTitle: "Stress",
    icon: "😰",
    description: "Assesses psychological stress, physical stress symptoms, and work-life burden.",
    instructions: "Over the last 2–4 weeks, how often have you experienced the following? Score: 0 = Rarely, 1 = Sometimes, 2 = Frequently.",
    category: ["Stress", "Mental Wellness", "Lifestyle"],
    womensHealthContext: ["PMS", "PCOS", "Menopause", "Postpartum", "Pregnancy"],
    scoring: {
      min: 0,
      max: 12,
      interpretation: { clinicalThreshold: 4 },
      severity: [
        { id: "low",      label: "Low Stress",      min: 0, max: 3,  color: "#16A34A", recommendation: "Good stress management. Continue healthy coping strategies." },
        { id: "moderate", label: "Moderate Stress",  min: 4, max: 7,  color: "#D97706", recommendation: "Stress management programme or counselling recommended." },
        { id: "high",     label: "High Stress",      min: 8, max: 12, color: "#DC2626", recommendation: "Mental health professional consultation recommended." },
      ],
    },
    questions: [
      { id: "q1", order: 1, text: "Do you feel overwhelmed by daily responsibilities or unable to manage tasks effectively?",                            clinicalTag: "overwhelmed",         options: RARELY_SOMETIMES },
      { id: "q2", order: 2, text: "Have you felt anxious, emotionally drained, irritable, or emotionally exhausted?",                                   clinicalTag: "emotional_stress",    options: RARELY_SOMETIMES },
      { id: "q3", order: 3, text: "Has stress caused difficulty falling asleep, night awakenings, or poor sleep quality?",                              clinicalTag: "stress_sleep",        options: NO_OCCASIONALLY_FREQ },
      { id: "q4", order: 4, text: "Do you experience stress-related physical symptoms such as headaches, muscle tension, fatigue, or low energy?",       clinicalTag: "physical_symptoms",   options: RARELY_SOMETIMES },
      { id: "q5", order: 5, text: "Do hormonal changes (PMS, PCOS, pregnancy, postpartum, or menopause symptoms) increase your stress levels?",         clinicalTag: "hormonal_stress",     options: [{ id: "no", label: "No", value: 0 }, { id: "mild", label: "Mild impact", value: 1 }, { id: "sig", label: "Significant impact", value: 2 }] },
      { id: "q6", order: 6, text: "Do work pressure, household duties, childcare, or caregiving responsibilities cause significant stress?",             clinicalTag: "work_life_stress",    options: RARELY_SOMETIMES },
    ],
  },

  /* ──────────────────────────────────────────────────────────
     11. Social Determinants Assessment
  ────────────────────────────────────────────────────────── */
  social: {
    id: "social_screen",
    type: "social_determinants",
    title: "Social Determinants of Health",
    shortTitle: "Social",
    icon: "🤝",
    description: "Identifies social, economic, and environmental factors affecting health — aligned with WHO frameworks.",
    instructions: "Over the last 6–12 months, choose the option that best applies. Score: 0 = No issue/Stable, 1 = Mild concern, 2 = Significant concern.",
    category: ["Social Health", "Preventive Care", "General Wellness"],
    womensHealthContext: ["All Women", "Pregnancy", "Postpartum"],
    scoring: {
      min: 0,
      max: 16,
      interpretation: { clinicalThreshold: 5 },
      severity: [
        { id: "low",      label: "Low Social Risk",      min: 0,  max: 4,  color: "#16A34A", recommendation: "Good social support. Maintain connections and access to care." },
        { id: "moderate", label: "Moderate Social Risk",  min: 5,  max: 9,  color: "#D97706", recommendation: "Social worker or community health liaison referral recommended." },
        { id: "high",     label: "High Social Risk",      min: 10, max: 16, color: "#DC2626", recommendation: "Urgent social support services and care coordination needed." },
      ],
    },
    questions: [
      {
        id: "q1", order: 1, text: "Do you have safe and stable housing?",
        clinicalTag: "housing_stability",
        options: [
          { id: "stable",    label: "Stable and safe",           value: 0 },
          { id: "concerns",  label: "Some housing concerns",     value: 1 },
          { id: "unstable",  label: "Unstable or unsafe housing", value: 2 },
        ],
      },
      {
        id: "q2", order: 2, text: "Do financial difficulties affect your ability to meet daily needs (food, medicines, healthcare, childcare)?",
        clinicalTag: "financial_stress",
        options: NO_SOMETIMES_FREQ,
      },
      {
        id: "q3", order: 3, text: "Do you have regular access to nutritious food?",
        clinicalTag: "food_security",
        options: [
          { id: "yes",       label: "Yes",                  value: 0 },
          { id: "limited",   label: "Limited at times",     value: 1 },
          { id: "inadequate",label: "Frequently inadequate", value: 2 },
        ],
      },
      {
        id: "q4", order: 4, text: "Do transportation, distance, cost, or scheduling issues prevent you from attending healthcare appointments?",
        clinicalTag: "healthcare_access",
        options: NO_OCCASIONALLY_FREQ,
      },
      {
        id: "q5", order: 5, text: "Do you feel emotionally supported by family, friends, or community?",
        clinicalTag: "social_support",
        options: [
          { id: "strong",  label: "Strong support",     value: 0 },
          { id: "limited", label: "Limited support",    value: 1 },
          { id: "none",    label: "No reliable support", value: 2 },
        ],
      },
      {
        id: "q6", order: 6, text: "Do you feel physically and emotionally safe at home and in your relationships?",
        clinicalTag: "domestic_safety",
        options: [
          { id: "safe",      label: "Safe",                         value: 0 },
          { id: "sometimes", label: "Occasionally unsafe or stressed", value: 1 },
          { id: "unsafe",    label: "Frequently unsafe or abusive",  value: 2 },
        ],
      },
      {
        id: "q7", order: 7, text: "Do you feel confident understanding health information and medical advice?",
        clinicalTag: "health_literacy",
        options: [
          { id: "yes",       label: "Yes",                    value: 0 },
          { id: "sometimes", label: "Sometimes difficult",    value: 1 },
          { id: "frequently",label: "Frequently difficult",   value: 2 },
        ],
      },
      {
        id: "q8", order: 8, text: "Do work responsibilities, childcare, or caregiving duties negatively affect your health or wellbeing?",
        clinicalTag: "caregiving_burden",
        options: RARELY_SOMETIMES,
      },
    ],
  },
};

/* ══════════════════════════════════════════════════════════════
   ASSESSMENT TYPES  (id → questionnaire key mapping)
══════════════════════════════════════════════════════════════ */

export const ASSESSMENT_TYPES = {
  depression: { id: "depression", questionnaireId: "phq9",            category: "mental_health"          },
  anxiety:    { id: "anxiety",    questionnaireId: "gad7",            category: "mental_health"          },
  fertility:  { id: "fertility",  questionnaireId: "fertility_screen", category: "reproductive_health"    },
  thyroid:    { id: "thyroid",    questionnaireId: "thyroid_screen",  category: "hormonal_metabolic"     },
  lifestyle:  { id: "lifestyle",  questionnaireId: "lifestyle_screen", category: "lifestyle_preventive"  },
  hormonal:   { id: "hormonal",   questionnaireId: "hormonal_screen", category: "hormonal_reproductive"  },
  diabetes:   { id: "diabetes",   questionnaireId: "diabetes_screen", category: "metabolic_endocrine"    },
  bmi:        { id: "bmi",        questionnaireId: "bmi_screen",      category: "metabolic"              },
  sleep:      { id: "sleep",      questionnaireId: "sleep_screen",    category: "sleep_wellness"         },
  stress:     { id: "stress",     questionnaireId: "stress_screen",   category: "mental_health_lifestyle"},
  social:     { id: "social",     questionnaireId: "social_screen",   category: "social_determinants"    },
};

/* ══════════════════════════════════════════════════════════════
   SYMPTOM CLUSTERS  (for StepSymptomsCluster routing)
══════════════════════════════════════════════════════════════ */

export const SYMPTOM_CLUSTERS = [
  {
    id: "mental_health",
    label: "Mental Health",
    symptoms: ["anxiety", "stress", "depression", "panic attacks", "fatigue", "insomnia", "mood swings", "irritability"],
    assessments: ["depression", "anxiety", "stress"],
  },
  {
    id: "reproductive_hormonal",
    label: "Reproductive & Hormonal",
    symptoms: ["irregular periods", "pcos", "heavy bleeding", "pelvic pain", "hormonal imbalance", "fertility"],
    assessments: ["fertility", "hormonal", "thyroid"],
  },
  {
    id: "metabolic",
    label: "Metabolic & Weight",
    symptoms: ["weight gain", "obesity", "blood sugar", "diabetes", "thyroid", "waist circumference"],
    assessments: ["diabetes", "bmi", "thyroid", "hormonal"],
  },
  {
    id: "sleep_fatigue",
    label: "Sleep & Fatigue",
    symptoms: ["insomnia", "poor sleep", "night awakenings", "daytime fatigue", "not refreshed"],
    assessments: ["sleep", "stress", "depression"],
  },
  {
    id: "lifestyle_preventive",
    label: "Lifestyle & Prevention",
    symptoms: ["sedentary", "poor diet", "smoking", "alcohol", "no checkup", "preventive"],
    assessments: ["lifestyle", "general"],
  },
  {
    id: "social_environmental",
    label: "Social & Environmental",
    symptoms: ["housing", "financial stress", "food insecurity", "social isolation", "caregiving"],
    assessments: ["social"],
  },
];

/* ══════════════════════════════════════════════════════════════
   SEVERITY & TRIAGE HELPERS
══════════════════════════════════════════════════════════════ */

export const getSeverityByScore = (score, severityRanges = []) =>
  severityRanges.find((item) => score >= item.min && score <= item.max);

export const getTriageLevel = ({ screeningResults = [] }) => {
  let level = "low";
  let urgent = false;
  screeningResults.forEach((result) => {
    if (result?.assessmentId === "depression" && result?.answers?.q9 > 0) urgent = true;
    if (result?.score >= 15) level = "high";
    else if (result?.score >= 10 && level !== "high") level = "medium";
  });
  return { level, urgent };
};

/* ══════════════════════════════════════════════════════════════
   SCREENING ENGINE FUNCTIONS
══════════════════════════════════════════════════════════════ */

export function normalizeText(text = "") {
  return text.toString().trim().toLowerCase();
}

/**
 * getMatchingAssessments
 * Returns array of { key, config } for all assessments whose keywords
 * match the provided symptoms list.
 * symptoms: Array<{ id, label }> | Array<string>
 */
export function getMatchingAssessments(symptoms = []) {
  if (!Array.isArray(symptoms) || symptoms.length === 0) return [];

  const normalizedSymptoms = symptoms.map((s) =>
    normalizeText(typeof s === "string" ? s : s?.label || s?.value || "")
  );

  const matches = [];
  Object.entries(ASSESSMENT_SYMPTOM_MAP).forEach(([key, keywords]) => {
    const found = keywords.some((kw) =>
      normalizedSymptoms.some((sym) => sym.includes(normalizeText(kw)))
    );
    if (found && SCREENING_QUESTIONNAIRES[key]) {
      matches.push({ key, config: SCREENING_QUESTIONNAIRES[key] });
    }
  });
  return matches;
}

/**
 * getSeverityLabel
 * Returns the severity object matching a given score.
 */
export function getSeverityLabel(score, severityConfig = []) {
  return (
    severityConfig.find((item) => score >= item.min && score <= item.max) || {
      label: "Unknown", min: 0, max: 0,
    }
  );
}

/**
 * calculateAssessmentScore
 * answers shape: { q1: { id, label, value }, q2: { … }, … }
 * Returns full result object consumed by StepTriage, StepResults.
 */
export function calculateAssessmentScore(questionnaire, answers = {}, questionnaireKey = "") {
  if (!questionnaire) return null;

  const questions = questionnaire.questions || [];
  let totalScore = 0;
  const criticalFlags = [];

  questions.forEach((question) => {
    const answer = answers?.[question.id];
    const value  = typeof answer?.value === "number" ? answer.value : 0;
    totalScore  += value;

    if (question.critical && value > 0) {
      criticalFlags.push({
        questionId: question.id,
        question:   question.text,
        value,
        alert:      "Immediate clinical attention recommended",
      });
    }
  });

  const severityMeta = getSeverityLabel(totalScore, questionnaire.scoring?.severity || []);

  const needsClinicalAttention =
    (totalScore >= (questionnaire.scoring?.interpretation?.clinicalThreshold ?? 999)) ||
    criticalFlags.length > 0;

  return {
    questionnaireId:        questionnaire.id,
    questionnaireKey,
    title:                  questionnaire.title,
    shortTitle:             questionnaire.shortTitle,
    totalScore,
    maxScore:               questionnaire.scoring?.max || 0,
    severity:               severityMeta.label,
    severityMeta,
    needsClinicalAttention,
    criticalFlags,
    answeredCount:          Object.keys(answers || {}).length,
    completed:              Object.keys(answers || {}).length === questions.length,
  };
}

/**
 * calculateAllAssessmentScores
 * assessments: [{ key, config }]
 * answers: { depression: { q1: {…}, … }, anxiety: { … } }
 */
export function calculateAllAssessmentScores(assessments = [], answers = {}) {
  return assessments.map((a) =>
    calculateAssessmentScore(a.config, answers?.[a.key] || {}, a.key)
  );
}

/** Returns the assessment result with the worst severity label. */
const SEVERITY_ORDER = ["Minimal", "Mild", "Low Risk", "Moderate Risk", "Moderate", "Moderately Severe", "High Risk", "Severe", "Very High"];

export function getHighestSeverityResult(results = []) {
  if (!results.length) return null;
  return results.reduce((highest, current) => {
    if (!current) return highest;
    if (!highest) return current;
    const ci = SEVERITY_ORDER.indexOf(current.severity);
    const hi = SEVERITY_ORDER.indexOf(highest.severity);
    return ci > hi ? current : highest;
  });
}

/**
 * getScreeningTriage
 * Converts scored results into a triage decision object.
 */
export function getScreeningTriage(results = []) {
  const highest = getHighestSeverityResult(results);

  if (!highest) return { code: "routine", label: "Routine", color: "#16A34A" };

  // Critical suicidal ideation
  if (highest.criticalFlags?.length > 0) {
    return { code: "urgent", label: "Immediate Mental Health Attention", color: "#DC2626", urgent: true };
  }

  // Severe / Moderately Severe
  if (["Severe", "Moderately Severe", "High Risk"].includes(highest.severity)) {
    return { code: "urgent", label: "High Priority Follow-up", color: "#EA580C", urgent: true };
  }

  // Moderate
  if (["Moderate", "Moderate Risk"].includes(highest.severity)) {
    return { code: "semi", label: "Clinical Evaluation Recommended", color: "#CA8A04" };
  }

  // Mild / Minimal / Low
  return { code: "routine", label: "Routine Monitoring", color: "#16A34A" };
}

/* ══════════════════════════════════════════════════════════════
   VOICE HOOK  (useVoiceSymptoms)
══════════════════════════════════════════════════════════════ */

export function matchHealthKeywords(transcript) {
  const lower = transcript.toLowerCase();
  const matched = [];
  ALL_HEALTH_KEYWORDS.forEach((kw) => {
    if (lower.includes(kw)) {
      const label = kw.replace(/\b\w/g, (c) => c.toUpperCase());
      if (!matched.find((m) => m.label === label)) {
        matched.push({ id: Date.now() + Math.random(), label });
      }
    }
  });
  return matched;
}

export function useVoiceSymptoms({ onDetected }) {
  const [isListening, setIsListening] = useState(false);
  const [liveText,    setLiveText]    = useState("");
  const [micError,    setMicError]    = useState("");
  const recRef     = useRef(null);
  const silenceRef = useRef(null);

  const supported =
    typeof window !== "undefined" &&
    ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  const stop = useCallback(() => {
    recRef.current?.stop();
    setIsListening(false);
    clearTimeout(silenceRef.current);
  }, []);

  const start = useCallback(() => {
    if (!supported) { setMicError("Voice input not supported in this browser."); return; }
    setMicError(""); setLiveText("");
    const SR  = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";
    recRef.current = rec;

    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++)
        interim += e.results[i][0].transcript;
      setLiveText(interim);
      clearTimeout(silenceRef.current);
      silenceRef.current = setTimeout(() => {
        if (interim.trim()) onDetected(interim.trim());
        setLiveText(""); stop();
      }, 1800);
    };

    rec.onerror = (e) => {
      setMicError(e.error === "not-allowed" ? "Microphone access denied." : `Error: ${e.error}`);
      setIsListening(false);
    };

    rec.onend   = () => setIsListening(false);
    rec.start();
    setIsListening(true);
  }, [supported, onDetected, stop]);

  const toggleMic = useCallback(
    () => (isListening ? stop() : start()),
    [isListening, stop, start]
  );

  return { isListening, liveText, micError, toggleMic, supported };
}

/* ══════════════════════════════════════════════════════════════
   SYMPTOM SET HELPERS
══════════════════════════════════════════════════════════════ */

export function addSymptom(prev, label) {
  return prev.find((s) => s.label === label)
    ? prev
    : [...prev, { id: Date.now() + Math.random(), label }];
}

export function removeSymptom(prev, label) {
  return prev.filter((s) => s.label !== label);
}

export function toggleSymptom(prev, label) {
  return prev.find((s) => s.label === label)
    ? prev.filter((s) => s.label !== label)
    : [...prev, { id: Date.now() + Math.random(), label }];
}